import { useState, useEffect, useCallback } from 'react';
import { CreditCard, Eye } from 'lucide-react';
import { paymentsApi, Payment } from '../../api/payments';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Sheet, SheetBody, SheetContent, SheetDescription, SheetHeader } from '../../components/ui/sheet';
import { Pagination } from '../../components/shared/Pagination';
import {
  EmptyState,
  ErrorState,
  FilterChips,
  FormField,
  PageHeader,
  StatusPill,
  Toolbar,
  type FilterChip, UserLink } from '@/components/app';
import { getErrorMessage } from '@/lib/errors';
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback';
import { PAYMENT_PROVIDER_LABELS, PAYMENT_STATUS_LABELS, labelOf } from '@/lib/labels';
import { paymentStatusTone } from '@/lib/status';
import {
  RowsSkeleton,
  SearchInput,
  SheetItem,
  SheetSection,
  SheetSkeleton,
  formatAmount,
  formatDateTime,
  shortId,
} from '../Orders/listParts';

interface PaymentsPageProps {
  /** Внутри раздела «Заказы и платежи»: заголовок страницы рисует обёртка. */
  embedded?: boolean;
}

const ITEMS_PER_PAGE = 10;
const STATUSES = ['initiated', 'success', 'failed', 'expired', 'cancelled'] as const;
const PROVIDERS = ['click', 'payme', 'paylov'] as const;

function PaymentStatus({ status }: { status: string }) {
  return (
    <StatusPill tone={paymentStatusTone(status)} dot>
      {labelOf(PAYMENT_STATUS_LABELS, status)}
    </StatusPill>
  );
}

function Provider({ provider }: { provider: string }) {
  return <Badge variant="outline">{labelOf(PAYMENT_PROVIDER_LABELS, provider)}</Badge>;
}

export default function PaymentsPage({ embedded = false }: PaymentsPageProps) {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [providerFilter, setProviderFilter] = useState<string>('all');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Детали платежа — правая панель.
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detail, setDetail] = useState<Payment | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const fetchPayments = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params: any = { page: currentPage, limit: ITEMS_PER_PAGE };
      if (searchQuery) params.search = searchQuery;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (providerFilter !== 'all') params.provider = providerFilter;
      const response = await paymentsApi.getAll(params);
      setPayments(response.items);
      setTotalPages(response.totalPages);
      setTotal(response.total);
    } catch (error: any) {
      setError(getErrorMessage(error, 'Не удалось загрузить платежи'));
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, searchQuery, statusFilter, providerFilter]);

  useEffect(() => { fetchPayments(); }, [fetchPayments]);

  // Запрос уходит через 300 мс после последнего символа; страница сбрасывается на первую.
  const applySearch = useDebouncedCallback((value: string) => { setSearchQuery(value); setCurrentPage(1); });
  const handleSearch = (value: string) => { setSearchInput(value); applySearch(value); };
  const handleStatusFilter = (value: string) => { setStatusFilter(value); setCurrentPage(1); };
  const handleProviderFilter = (value: string) => { setProviderFilter(value); setCurrentPage(1); };
  const clearSearch = () => { setSearchInput(''); setSearchQuery(''); setCurrentPage(1); };
  const resetFilters = () => { clearSearch(); setStatusFilter('all'); setProviderFilter('all'); };

  const loadDetail = useCallback(async (id: string) => {
    setDetailLoading(true);
    setDetailError(null);
    try {
      setDetail(await paymentsApi.getById(id));
    } catch (error: any) {
      setDetailError(getErrorMessage(error, 'Не удалось загрузить детали платежа'));
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const handleView = (payment: Payment) => {
    setDetailId(payment.id);
    setDetail(null);
    void loadDetail(payment.id);
  };

  const chips: FilterChip[] = [
    ...(searchQuery ? [{ key: 'search', label: `Поиск: ${searchQuery}`, onRemove: clearSearch, removeLabel: 'Убрать фильтр: поиск' }] : []),
    ...(statusFilter !== 'all'
      ? [{
          key: 'status',
          label: `Статус: ${labelOf(PAYMENT_STATUS_LABELS, statusFilter)}`,
          onRemove: () => handleStatusFilter('all'),
          removeLabel: 'Убрать фильтр: статус',
        }]
      : []),
    ...(providerFilter !== 'all'
      ? [{
          key: 'provider',
          label: `Провайдер: ${labelOf(PAYMENT_PROVIDER_LABELS, providerFilter)}`,
          onRemove: () => handleProviderFilter('all'),
          removeLabel: 'Убрать фильтр: провайдер',
        }]
      : []),
  ];

  return (
    <div className="flex flex-col gap-5">
      {!embedded && <PageHeader title="Платежи" subtitle="Платёжные транзакции провайдеров" />}

      <Card
        role="search"
        aria-label="Фильтры платежей"
        className="grid grid-cols-1 gap-3 px-[18px] py-4 sm:grid-cols-2 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)]"
      >
        <FormField label="Поиск" tone="filter" className="sm:col-span-2 lg:col-span-1">
          <SearchInput
            placeholder="Имя, email, телефон, ID платежа или провайдера"
            value={searchInput}
            onChange={(e) => handleSearch(e.target.value)}
          />
        </FormField>
        <FormField label="Статус" tone="filter">
          {(p) => (
            <Select value={statusFilter} onValueChange={handleStatusFilter}>
              <SelectTrigger {...p}>
                <SelectValue placeholder="Все статусы" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все статусы</SelectItem>
                {STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>{PAYMENT_STATUS_LABELS[s]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </FormField>
        <FormField label="Провайдер" tone="filter">
          {(p) => (
            <Select value={providerFilter} onValueChange={handleProviderFilter}>
              <SelectTrigger {...p}>
                <SelectValue placeholder="Все провайдеры" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все провайдеры</SelectItem>
                {PROVIDERS.map((s) => (
                  <SelectItem key={s} value={s}>{PAYMENT_PROVIDER_LABELS[s]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </FormField>
      </Card>

      <Card className="overflow-hidden" role="region" aria-label="Список платежей">
        <Toolbar title={<>Найдено <span className="tabular-nums">{total.toLocaleString('ru-RU')}</span></>}>
          <FilterChips chips={chips} onReset={resetFilters} />
        </Toolbar>

        {isLoading ? (
          <RowsSkeleton label="Загрузка платежей" />
        ) : error ? (
          <ErrorState message={error} onRetry={fetchPayments} className="border-t border-border-soft" />
        ) : payments.length === 0 ? (
          <EmptyState
            className="border-t border-border-soft"
            icon={<CreditCard />}
            title="Платежи не найдены"
            description={chips.length ? 'Попробуйте изменить или сбросить фильтры.' : undefined}
            action={chips.length ? <Button variant="secondary" onClick={resetFilters}>Сбросить фильтры</Button> : undefined}
          />
        ) : (
          <Table className="min-w-[860px]">
            <TableHeader>
              <TableRow>
                <TableHead>Платёж</TableHead>
                <TableHead>Пользователь</TableHead>
                <TableHead>Провайдер</TableHead>
                <TableHead className="hidden md:table-cell">Заказ</TableHead>
                <TableHead className="text-right">Сумма</TableHead>
                <TableHead>Статус</TableHead>
                <TableHead className="hidden sm:table-cell">Создан</TableHead>
                <TableHead className="text-right"><span className="sr-only">Действия</span></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payments.map((payment) => (
                <TableRow key={payment.id} selected={detailId === payment.id}>
                  <TableCell className="font-mono text-xs text-text-2">{shortId(payment.id)}</TableCell>
                  <TableCell className="max-w-[220px]"><UserLink user={payment.order?.user} /></TableCell>
                  <TableCell><Provider provider={payment.provider} /></TableCell>
                  <TableCell className="hidden font-mono text-xs text-text-2 md:table-cell">{shortId(payment.order_id)}</TableCell>
                  <TableCell className="whitespace-nowrap text-right font-display font-bold tabular-nums">
                    {formatAmount(payment.amount, payment.currency)}
                  </TableCell>
                  <TableCell><PaymentStatus status={payment.status} /></TableCell>
                  <TableCell className="hidden whitespace-nowrap tabular-nums text-text-2 sm:table-cell">{formatDateTime(payment.created_at)}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="secondary" size="icon-sm" aria-label={`Детали платежа ${shortId(payment.id)}`} onClick={() => handleView(payment)}>
                      <Eye />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {!isLoading && !error && payments.length > 0 && (
          <Pagination currentPage={currentPage} totalPages={totalPages} total={total} itemsPerPage={ITEMS_PER_PAGE} onPageChange={setCurrentPage} />
        )}
      </Card>

      <Sheet open={detailId !== null} onOpenChange={(open) => { if (!open) setDetailId(null); }}>
        <SheetContent>
          <SheetHeader title="Детали платежа" />
          <SheetDescription className="sr-only">Полная информация о платеже</SheetDescription>
          <SheetBody>
            {detailLoading ? (
              <SheetSkeleton />
            ) : detailError ? (
              <ErrorState message={detailError} onRetry={() => detailId && void loadDetail(detailId)} />
            ) : detail ? (
              <>
                <section aria-label="Итог" className="flex items-start justify-between gap-3 rounded-card border border-border-soft bg-surface px-4 py-3.5 shadow-card">
                  <div className="flex min-w-0 flex-col gap-1">
                    <span className="text-xs text-text-2">Сумма</span>
                    <b className="font-display text-[22px] font-extrabold tabular-nums">{formatAmount(detail.amount, detail.currency)}</b>
                    <span className="text-[13px] text-text-2">
                      {labelOf(PAYMENT_PROVIDER_LABELS, detail.provider)} · <span className="tabular-nums">{formatDateTime(detail.created_at)}</span>
                    </span>
                  </div>
                  <PaymentStatus status={detail.status} />
                </section>
                <SheetSection title="Пользователь">
                  <SheetItem label="Пользователь" wide><UserLink user={detail.order?.user} /></SheetItem>
                  <SheetItem label="Тариф">{detail.order?.plan?.title || '—'}</SheetItem>
                </SheetSection>
                <SheetSection title="Платёж">
                  <SheetItem label="ID платежа" mono wide>{detail.id}</SheetItem>
                  <SheetItem label="Провайдер"><Provider provider={detail.provider} /></SheetItem>
                  <SheetItem label="ID платежа провайдера" mono>{detail.provider_payment_id || '—'}</SheetItem>
                  <SheetItem label="ID заказа" mono wide>{detail.order_id}</SheetItem>
                  <SheetItem label="Создан"><span className="tabular-nums">{formatDateTime(detail.created_at)}</span></SheetItem>
                </SheetSection>
                {detail.raw_response && (
                  <section aria-label="Сырой ответ" className="flex flex-col gap-3 rounded-card border border-border-soft bg-surface px-4 py-3.5 shadow-card">
                    <h3 className="m-0 font-display text-[15px] font-bold">Сырой ответ провайдера</h3>
                    <pre className="m-0 max-h-80 overflow-auto rounded-xl bg-surface-alt p-3 font-mono text-xs text-text">
                      {JSON.stringify(detail.raw_response, null, 2)}
                    </pre>
                  </section>
                )}
              </>
            ) : null}
          </SheetBody>
        </SheetContent>
      </Sheet>
    </div>
  );
}
